/**
 * #345 — the "Open orders — this game" panel's orchestration, extracted from
 * `GameOpenOrders` so the component is presentation only.
 *
 * What lives here: the store reads, the Polymarket poll, the memos that turn
 * live order arrays into rows, and the five commands. What deliberately does
 * NOT live here: the row shaping itself, which sits in `gameOpenOrderRows` so
 * it can be tested without a store mock in sight.
 *
 * Four properties this file is responsible for preserving exactly:
 *
 *   1. The DISPLAY lists and the DESTRUCTIVE scope stay separately derived.
 *      `kalshiRows` carries a moneyline-ticker fallback so a row still draws
 *      before its market frame streams in; `selectExactGameOrders` refuses that
 *      fallback because a ticker match is not proof of which game an order
 *      belongs to. Collapsing them into one selector would let an unidentified
 *      order receive a DELETE. They are not duplicate logic (#272).
 *   2. `openGameCancel` freezes the target set at the CLICK. The snapshot is
 *      the modal state — a boolean would re-read the live arrays at confirm
 *      time and send a set the trader never saw.
 *   3. `runBulkCancel`'s re-entry guard is a ref, not state. React state lands
 *      a tick late and a double-click on confirm lands in the same tick.
 *   4. The per-row pending fence is read from the STORE, never mirrored into
 *      local state. A successful DELETE holds the row disabled until an
 *      authoritative snapshot drops the ID; a local flag cleared in a `finally`
 *      is the #241 defect.
 *
 * `isEmpty` reports the render-nothing case rather than the hook returning
 * early — hooks cannot branch, and the caller needs the flag anyway.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useOscarAccess } from '../../hooks/useOscarAccess';
import {
  usePolymarketRelayOrders,
  invalidatePolyOrdersShare,
} from '@/hooks/usePolymarketRelayOrders';
import { cancelPolyOrderViaRelay } from '@/lib/polymarket/relay';
import { describePolyOrderCached } from '@/lib/polymarket/describeOrder';
import { isKalshiCancelPending } from '../../lib/trading/kalshiCancelLifecycle';
import {
  freezeCancelSnapshot,
  selectExactGameOrders,
  type FrozenCancelSnapshot,
  type GameOrderBlocker,
} from '@/lib/trading/gameOrderSelection';
import {
  executeBulkOrderCancel,
  type BulkCancelReport,
} from '@/lib/trading/bulkOrderCancel';
import {
  kalshiOrderMatchesPanel,
  polyOrderMatchesPanel,
  projectKalshiOpenOrderRow,
  projectPolyOpenOrderRow,
  type KalshiOpenOrderRow,
  type PolyOpenOrderRow,
} from './gameOpenOrderRows';
import type { OscarReleasePrefill } from './oscar';
import type { GameRow } from './types';

/** A cancel whose confirmation the venue never sent. Not a failure — the
 *  DELETE succeeded — so the row keeps its control and shows this instead. */
export interface TimedOutCancelNotice {
  orderId: string;
  message: string;
}

/** The per-row cancel awaiting the trader's approval. */
export interface PendingRowCancel {
  orderId: string;
  label: string;
}

export interface GameOpenOrdersModel {
  /** No rows, no blockers, no report — the panel draws no section at all. */
  isEmpty: boolean;

  // ── Rows ──
  kalshiRows: readonly KalshiOpenOrderRow[];
  polyRows: readonly PolyOpenOrderRow[];
  /** Whether this order's cancel controls are fenced. A timed-out cancel is
   *  deliberately NOT pending — the trader gets the control back. */
  isCancelPending: (orderId: string) => boolean;
  timedOutNotices: readonly TimedOutCancelNotice[];

  // ── Per-row cancel ──
  cancelError: string | null;
  pendingRowCancel: PendingRowCancel | null;
  /** Opens the confirmation, or cancels immediately when the trader has turned
   *  confirm-before-cancel off. */
  requestRowCancel: (orderId: string, label: string) => void;
  confirmRowCancel: () => void;
  dismissRowCancel: () => void;

  // ── Game-wide cancel ──
  blockers: readonly GameOrderBlocker[];
  canBulkCancel: boolean;
  bulkRunning: boolean;
  bulkReport: BulkCancelReport | null;
  /** The frozen set under confirmation. Non-null IS the modal being open. */
  pendingGameCancel: FrozenCancelSnapshot | null;
  /** Why the action is enabled or not, in the trader's words. */
  bulkCancelTitle: string;
  openGameCancel: () => void;
  confirmGameCancel: () => void;
  dismissGameCancel: () => void;
  retryFailedCancels: () => void;
}

export interface GameOpenOrdersModelInput {
  game: GameRow;
  /** Present only when the panel can accept an armed release. Folded into the
   *  row projection's gate together with the trader's oscar access. */
  onOscarRelease?: (prefill: OscarReleasePrefill) => void;
}

export function useGameOpenOrdersModel({
  game,
  onOscarRelease,
}: GameOpenOrdersModelInput): GameOpenOrdersModel {
  const openOrders = useAccountStore((s) => s.openOrders);
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  const cancelOrders = useAccountStore((s) => s.cancelOrders);
  const refreshOrders = useAccountStore((s) => s.refreshOrders);
  const oscarAccess = useOscarAccess();
  // Honor the app-wide confirm-before-cancel setting (default ON) like every other
  // cancel surface — an inline card cancel must not bypass the trader's safety gate.
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  // Re-run the canonical-key match as frames arrive — a sibling market that
  // streams in after mount (spread/total tab not yet warmed) then matches.
  const marketVersion = useMarketStore((s) => s.version);
  // Poll Polymarket open orders only while this card is mounted (panel open).
  // The hook is inert in personal-key / no-cred modes (returns []), so the poly
  // section simply doesn't render then. Exactly ONE instance — the panel adds no
  // second poll.
  const { orders: polyOrders, refresh: refreshPolyOrders } = usePolymarketRelayOrders();
  // #241: the pending-cancel fence is shared store state, not local — a
  // successful DELETE keeps the row disabled until an authoritative snapshot
  // drops the ID, and the same fence has to hold on every mounted surface.
  const kalshiCancelStates = useAccountStore((s) => s.kalshiCancelStates);

  const [cancelError, setCancelError] = useState<string | null>(null);
  const [pendingRowCancel, setPendingRowCancel] = useState<PendingRowCancel | null>(null);
  // The snapshot IS the modal state — storing a boolean here is the bug this
  // replaces: a boolean modal would re-read the live arrays at confirm time and
  // send a set the trader never saw.
  const [pendingGameCancel, setPendingGameCancel] = useState<FrozenCancelSnapshot | null>(null);
  const [bulkReport, setBulkReport] = useState<BulkCancelReport | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  // Synchronous re-entry guard. React state lands a tick late; a double-click on
  // the confirm button lands in the same tick.
  const runningRef = useRef(false);

  const awayTicker = game.kalshi?.away?.marketTicker;
  const homeTicker = game.kalshi?.home?.marketTicker;
  const moneylineTickers = useMemo(
    () => new Set([awayTicker, homeTicker].filter((t): t is string => !!t)),
    [awayTicker, homeTicker],
  );

  // Kalshi: any resting order whose market resolves to this game's canonical
  // key (moneyline + spread + total + period), with the moneyline-ticker Set
  // as the fallback when the frame isn't loaded so canonical resolution misses.
  const orders = useMemo(() => {
    const store = useMarketStore.getState();
    return openOrders.filter((o) => {
      // Fail-closed resolution (DNL-001): an ambiguous doubleheader frame
      // resolves to NO game rather than being promoted to gameNo=1, which would
      // seat another game's resting order on this card. '' simply doesn't
      // match; the moneyline-ticker fallback below still covers a missing frame.
      const canon = store.getGameKeyV2ForTicker(o.ticker) ?? '';
      return kalshiOrderMatchesPanel(canon, o.ticker, game.key, moneylineTickers);
    });
    // marketVersion drives re-filtering as frames stream in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOrders, moneylineTickers, game.key, marketVersion]);

  // Labels, the side-native price and the blocker's release prefill. Split from
  // the filter above because it depends on oscar access rather than on which
  // markets have streamed in.
  const kalshiRows: KalshiOpenOrderRow[] = useMemo(
    () =>
      orders.map((ord) =>
        projectKalshiOpenOrderRow(ord, { releaseEnabled: oscarAccess && !!onOscarRelease }),
      ),
    [orders, oscarAccess, onOscarRelease],
  );

  // Polymarket: asset_id → ticker → market → canonical key === baseGameKey(game.key).
  // Display-only; labels come from the shared poly-order describer.
  const polyRows = useMemo(() => {
    const store = useMarketStore.getState();
    const matched: PolyOpenOrderRow[] = [];
    for (const po of polyOrders) {
      const assetId = typeof po.asset_id === 'string' ? po.asset_id : '';
      if (!assetId) continue;
      const ticker = store.getTickerByTokenId(assetId);
      const mkt = ticker ? store.getMarket(ticker) : null;
      if (!mkt) continue;
      // Same fail-closed resolution as the Kalshi branch (DNL-001). Poly is the
      // venue that actually produces ambiguous DH frames — its slug can only
      // address the matchup-date, not the game.
      const canon = store.getGameKeyV2ForTicker(ticker!) ?? '';
      if (!polyOrderMatchesPanel(canon, game.key)) continue;
      matched.push(
        projectPolyOpenOrderRow(po, {
          assetId,
          market: mkt,
          description: describePolyOrderCached(assetId),
        }),
      );
    }
    return matched;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polyOrders, game.key, marketVersion]);

  // ── #272: the DESTRUCTIVE scope, derived separately from the display lists ──
  // Deliberately not reused from `orders`/`polyRows` above: those carry the
  // display-only ticker fallback, which is fine for drawing a row and is not
  // proof of identity for a DELETE. The selector re-derives from the same live
  // own-scoped arrays under the exact-key rule and reports what must block.
  const cancelSelection = useMemo(
    () => selectExactGameOrders({ game, kalshiOrders: openOrders, polyOrders }),
    // marketVersion re-runs the resolution as frames stream in, exactly like the
    // display memos above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [game, openOrders, polyOrders, marketVersion],
  );

  const blockers = cancelSelection.blockers;
  const canBulkCancel =
    blockers.length === 0 && cancelSelection.targets.length > 0 && !bulkRunning;

  // Timed-out fences for orders visible on THIS card. Derived from shared state
  // rather than copied into local state, so a retry or an authoritative removal
  // clears the notice without a second code path.
  const timedOutNotices = useMemo(
    () =>
      orders.flatMap((ord) => {
        const state = kalshiCancelStates[ord.orderId];
        return state?.phase === 'timed-out'
          ? [{ orderId: ord.orderId, message: state.message ?? '' }]
          : [];
      }),
    [orders, kalshiCancelStates],
  );

  const isCancelPending = useCallback(
    (orderId: string) => isKalshiCancelPending(kalshiCancelStates[orderId]),
    [kalshiCancelStates],
  );

  const executeRowCancel = useCallback(
    async (orderId: string) => {
      setCancelError(null);
      try {
        // The store owns the pending state now — no local flag to clear in a
        // `finally`, which is what let the row re-enable before removal (#241).
        await cancelOrder(orderId);
      } catch (err) {
        // A failed cancel must NOT look like success on a live-game card — surface it.
        setCancelError(
          err instanceof Error && err.message
            ? err.message
            : 'Cancel failed. Check open orders and try again.',
        );
      }
    },
    [cancelOrder],
  );

  const requestRowCancel = useCallback(
    (orderId: string, label: string) => {
      setCancelError(null);
      if (confirmOrderCancel) {
        setPendingRowCancel({ orderId, label });
        return;
      }
      void executeRowCancel(orderId);
    },
    [confirmOrderCancel, executeRowCancel],
  );

  const confirmRowCancel = useCallback(() => {
    // Clear the target BEFORE firing: the store's duplicate guard is the
    // final safety net, but the modal shouldn't be able to re-submit.
    const target = pendingRowCancel;
    setPendingRowCancel(null);
    if (target) void executeRowCancel(target.orderId);
  }, [pendingRowCancel, executeRowCancel]);

  const dismissRowCancel = useCallback(() => setPendingRowCancel(null), []);

  const openGameCancel = useCallback(() => {
    if (!canBulkCancel) return;
    setBulkReport(null);
    // Frozen HERE, from the click — not read again when confirm fires.
    setPendingGameCancel(
      freezeCancelSnapshot({
        scope: 'game',
        label: cancelSelection.gameLabel,
        targets: cancelSelection.targets,
      }),
    );
  }, [canBulkCancel, cancelSelection]);

  const runBulkCancel = useCallback(
    async (snapshot: FrozenCancelSnapshot) => {
      if (runningRef.current) return;
      runningRef.current = true;
      setBulkRunning(true);
      try {
        const report = await executeBulkOrderCancel(snapshot, {
          // refreshAfter:false — this executor owns the single per-venue refresh.
          cancelKalshiOrders: (ids) => cancelOrders(ids, { refreshAfter: false }),
          requestPolymarketCancel: cancelPolyOrderViaRelay,
          refreshKalshi: () => refreshOrders(),
          refreshPolymarket: async () => {
            invalidatePolyOrdersShare();
            await refreshPolyOrders(true);
          },
        });
        setBulkReport(report);
      } finally {
        runningRef.current = false;
        setBulkRunning(false);
      }
    },
    [cancelOrders, refreshOrders, refreshPolyOrders],
  );

  const confirmGameCancel = useCallback(() => {
    const snapshot = pendingGameCancel;
    // Closed BEFORE the first await so the confirm button cannot be hit twice.
    setPendingGameCancel(null);
    if (snapshot) void runBulkCancel(snapshot);
  }, [pendingGameCancel, runBulkCancel]);

  const dismissGameCancel = useCallback(() => setPendingGameCancel(null), []);

  const retryFailedCancels = useCallback(() => {
    const failed = bulkReport?.failed ?? [];
    if (failed.length === 0 || bulkRunning) return;
    // Failures ONLY. A submitted ID resent here would re-DELETE a cancellation
    // that already worked and fail misleadingly (#241's original defect).
    setPendingGameCancel(
      freezeCancelSnapshot({
        scope: 'game',
        label: cancelSelection.gameLabel,
        targets: failed.map((row) => row.target),
      }),
    );
  }, [bulkReport, bulkRunning, cancelSelection]);

  const bulkCancelTitle =
    blockers.length > 0
      ? 'Cannot safely cancel this game — see the reason below'
      : cancelSelection.targets.length === 0
        ? 'No confirmed open orders for this game'
        : 'Cancel every confirmed open order for this game';

  return {
    // A finished bulk report keeps the section mounted after the last row has
    // gone: the outcome of a destructive action must outlive its targets.
    isEmpty:
      kalshiRows.length === 0 &&
      polyRows.length === 0 &&
      blockers.length === 0 &&
      bulkReport === null,
    kalshiRows,
    polyRows,
    isCancelPending,
    timedOutNotices,
    cancelError,
    pendingRowCancel,
    requestRowCancel,
    confirmRowCancel,
    dismissRowCancel,
    blockers,
    canBulkCancel,
    bulkRunning,
    bulkReport,
    pendingGameCancel,
    bulkCancelTitle,
    openGameCancel,
    confirmGameCancel,
    dismissGameCancel,
    retryFailedCancels,
  };
}
